import { generateUuid } from "./id";
import { getRootBodyUuid, loadSceneFromParsedResult } from "./sceneLoader";
import { getFileActions, useFileStore } from "../stores/fileStore";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { getGraspActions } from "../stores/graspStore";
import { recordMjcfRootDefaultPose } from "./mjcfRootDefaultPose";
import { applyPhysicsTimestepFromXmlFile } from "./mjcfOptions";
import { shouldBlockSceneAuthoringMutation } from "./physicsGuards";
import { normalizeAssetPath, resolveCritterAssetsUrl } from "./assetPaths";
import { libraryAssetHasGrasps } from "./libraryCatalog";
import { applyMjcfCatalogTransforms } from "./mjcfCatalogTransforms";
import { showNoRobotVisualsNoticeIfNeeded } from "./robotVisualNotice";
import { LIBRARY_ASSET_LOAD_PROGRESS } from "../constants/ui";
import { logger } from "./logger";
import {
  assertMeshFileWithinBrowserImportLimit,
  browserImportLimitIssueFromErrors,
  isBrowserImportLimitError,
  requestBrowserImportLimitProceedForIssue,
  showBrowserImportLimitIssue,
} from "./importLimits";
import { MJCFParser, UNSUPPORTED_PLUGIN_SURFACE_WARNING_PREFIX } from "../core/mjcfParser";
import { startSimulationFromCurrentFiles, stopSimulation } from "../physics/mujocoBridge";
import {
  applyAmbiguousGeomRoleSelections,
  getAmbiguousGeomRoleCandidatesByWarning,
} from "../core/mjcfAmbiguousGeomRoles";
import { fetchLibraryAssetBlobWithRetry } from "./libraryAssetFetch";

let nextCollisionTypeBit = 1;

const DEFAULT_SPAWN_KEYFRAME_NAME = "Standing";
const normalizeParseWarnings = (parsedResult) => {
  const warnings = parsedResult?.parseWarnings;
  if (!Array.isArray(warnings)) return [];
  return warnings.map((warning) => String(warning || "").trim()).filter(Boolean);
};

const shouldPromptForLibraryAssetAmbiguousRoles = (asset, fetched) =>
  asset?.category === "robot" ||
  asset?.category === "attachment" ||
  fetched?.folder === "robots" ||
  fetched?.folder === "attachments";

const promptForLibraryAssetImportWarnings = async ({ asset, fetched, parsedResult }) => {
  if (!shouldPromptForLibraryAssetAmbiguousRoles(asset, fetched)) return true;
  const warnings = normalizeParseWarnings(parsedResult).filter(
    (warning) =>
      warning.startsWith("Ambiguous mesh geom role:") ||
      warning.startsWith(UNSUPPORTED_PLUGIN_SURFACE_WARNING_PREFIX)
  );
  if (warnings.length === 0) return true;
  const ambiguousWarnings = warnings.filter((warning) =>
    warning.startsWith("Ambiguous mesh geom role:")
  );
  const candidates = getAmbiguousGeomRoleCandidatesByWarning(parsedResult, ambiguousWarnings);
  return new Promise((resolve, reject) => {
    getFileActions().promptPendingMjcfImportWarnings({
      fileName: asset?.name || fetched?.xmlFile?.name || "Library asset",
      warnings,
      onContinue: async (options) => {
        try {
          applyAmbiguousGeomRoleSelections(parsedResult, candidates, options?.ambiguousGeomRoles);
          resolve(true);
        } catch (error) {
          reject(error);
        }
      },
      onCancel: () => resolve(false),
    });
  });
};

const isVector3 = (value) =>
  Array.isArray(value) && value.length === 3 && value.every((n) => Number.isFinite(Number(n)));

const isQuaternion = (value) =>
  Array.isArray(value) && value.length === 4 && value.every((n) => Number.isFinite(Number(n)));

const normalizeTransformOverride = (transformOverride, bodyState) => {
  if (!transformOverride) return null;
  const bodyPosition = bodyState?.position;
  const bodyOrientation = bodyState?.orientation;
  const nextPosition = isVector3(transformOverride.position)
    ? transformOverride.position.map(Number)
    : isVector3(bodyPosition)
      ? [...bodyPosition]
      : null;
  const nextOrientation = isQuaternion(transformOverride.orientation)
    ? transformOverride.orientation.map(Number)
    : isQuaternion(bodyOrientation)
      ? [...bodyOrientation]
      : null;
  if (!nextPosition || !nextOrientation) return null;
  return {
    position: nextPosition,
    orientation: nextOrientation,
  };
};

/** @param {AbortSignal | null | undefined} signal */
export const libraryLoadThrowIfAborted = (signal) => {
  if (signal?.aborted) throw new globalThis.DOMException("Aborted", "AbortError");
};

const restartPhysicsIfNeeded = async (wasPhysicsRunning) => {
  if (!wasPhysicsRunning) return;
  try {
    await stopSimulation({ restoreScene: false });
    await new Promise((resolve) => setTimeout(resolve, 50));
    await startSimulationFromCurrentFiles({ preserveActuatorControls: true });
  } catch (restartError) {
    logger.warn("[libraryAssetLoader] Failed to restart physics after load", restartError);
  }
};

const showLibraryMeshLoadWarning = (failedMeshNames) => {
  if (!Array.isArray(failedMeshNames) || failedMeshNames.length === 0) return;
  if (typeof window === "undefined") return;
  const count = failedMeshNames.length;
  const firstMeshName = failedMeshNames.find(Boolean);
  const meshLabel = count === 1 ? "mesh" : "meshes";
  const detail = firstMeshName && count === 1 ? ` (${firstMeshName})` : "";
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: `${count} library ${meshLabel} could not be loaded${detail}; affected geometry was omitted.`,
        },
      })
    );
  } catch (error) {
    logger.warn("[libraryAssetLoader] Failed to dispatch mesh load warning toast", error);
  }
};

const parseXmlText = (xmlText) => new DOMParser().parseFromString(xmlText, "text/xml");

const normalizeRelativeAssetPath = (value) => normalizeAssetPath(value);

const ABSOLUTE_OR_ROOTED_URL_PATTERN = /^(?:[a-z][a-z0-9+.-]*:|\/\/|\/)/i;

const dirnameOfPath = (value) => {
  const normalized = normalizeRelativeAssetPath(value);
  const index = normalized.lastIndexOf("/");
  return index >= 0 ? normalized.slice(0, index) : "";
};

const hasParentDirectorySegment = (value) => {
  const normalized = normalizeRelativeAssetPath(value);
  const decoded = (() => {
    try {
      return decodeURIComponent(normalized);
    } catch {
      throw new Error(`Invalid MJCF include path encoding: ${value}`);
    }
  })();
  return decoded.split("/").some((part) => part === "..");
};

const joinRelativePath = (base, file) => {
  const normalizedFile = normalizeRelativeAssetPath(file);
  if (!normalizedFile) return "";
  if (/^https?:\/\//i.test(normalizedFile)) return normalizedFile;
  const normalizedBase = normalizeRelativeAssetPath(base);
  if (!normalizedBase) return normalizedFile;
  return `${normalizedBase}/${normalizedFile}`;
};

const joinAssetPath = (base, file) => {
  const normalizedFile = normalizeAssetPath(file);
  if (!normalizedFile) return "";
  if (/^https?:\/\//i.test(normalizedFile)) return normalizedFile;
  const normalizedBase = normalizeAssetPath(base, { stripTrailingSlash: true });
  if (!normalizedBase || normalizedFile.startsWith(`${normalizedBase}/`)) return normalizedFile;
  return `${normalizedBase}/${normalizedFile}`;
};

const resolveLibraryIncludeUrl = ({
  includeFile,
  currentXmlPath,
  folder,
  assetId,
  resolveCatalogUrl,
}) => {
  const trimmedIncludeFile = String(includeFile || "").trim();
  if (!trimmedIncludeFile) return null;
  if (ABSOLUTE_OR_ROOTED_URL_PATTERN.test(trimmedIncludeFile)) {
    throw new Error(`MJCF include must be a relative catalog path: ${trimmedIncludeFile}`);
  }
  if (hasParentDirectorySegment(trimmedIncludeFile)) {
    throw new Error(`MJCF include escapes catalog asset directory: ${trimmedIncludeFile}`);
  }

  const includePath = joinRelativePath(dirnameOfPath(currentXmlPath), trimmedIncludeFile);
  if (!includePath) return null;
  if (hasParentDirectorySegment(includePath)) {
    throw new Error(`MJCF include escapes catalog asset directory: ${trimmedIncludeFile}`);
  }

  const urlBase = globalThis.location?.href || "http://localhost/";
  const catalogBaseUrl = new URL(resolveCatalogUrl(`/${folder}/${assetId}/`), urlBase);
  const includeUrl = new URL(resolveCatalogUrl(`/${folder}/${assetId}/${includePath}`), urlBase);
  const catalogBasePath = catalogBaseUrl.pathname.endsWith("/")
    ? catalogBaseUrl.pathname
    : `${catalogBaseUrl.pathname}/`;
  if (
    includeUrl.origin !== catalogBaseUrl.origin ||
    !includeUrl.pathname.startsWith(catalogBasePath)
  ) {
    throw new Error(`MJCF include resolved outside catalog asset directory: ${trimmedIncludeFile}`);
  }

  return {
    includePath,
    includeUrl: includeUrl.href,
  };
};

const fetchAssetTargets = async ({
  targets,
  folder,
  assetId,
  resolveCatalogUrl,
  signal,
  report,
  progressStart,
  progressSpan,
  buildResult,
}) => {
  const total = targets.length;
  let done = 0;
  const bumpProgress = () => {
    done += 1;
    if (total > 0) report(progressStart + progressSpan * (done / total));
  };

  return Promise.allSettled(
    targets.map(async (target) => {
      try {
        const assetPath = /^https?:\/\//i.test(target.actualFilePath)
          ? target.actualFilePath
          : resolveCatalogUrl(`/${folder}/${assetId}/${target.actualFilePath}`);
        const blob = await fetchLibraryAssetBlobWithRetry(assetPath, { signal });
        const result = buildResult(target, blob);
        bumpProgress();
        return result;
      } catch (err) {
        bumpProgress();
        throw err;
      }
    })
  );
};

const expandMjcfIncludes = async ({
  xmlText,
  xmlPath,
  folder,
  assetId,
  resolveCatalogUrl,
  signal,
  visited = new Set(),
}) => {
  const normalizedXmlPath = normalizeRelativeAssetPath(xmlPath);
  if (visited.has(normalizedXmlPath)) {
    logger.warn("[libraryAssetLoader] Skipping recursive MJCF include", normalizedXmlPath);
    return xmlText;
  }
  visited.add(normalizedXmlPath);

  const xmlDoc = parseXmlText(xmlText);
  const includeElements = Array.from(xmlDoc.querySelectorAll("include[file]"));
  if (includeElements.length === 0) return xmlText;

  for (const includeElement of includeElements) {
    libraryLoadThrowIfAborted(signal);
    const resolvedInclude = resolveLibraryIncludeUrl({
      includeFile: includeElement.getAttribute("file"),
      currentXmlPath: normalizedXmlPath,
      folder,
      assetId,
      resolveCatalogUrl,
    });
    if (!resolvedInclude) continue;
    const { includePath, includeUrl } = resolvedInclude;
    const includeResponse = await fetch(includeUrl, { cache: "no-store", signal });
    if (!includeResponse.ok) {
      throw new Error(`Failed to load MJCF include ${includePath}: HTTP ${includeResponse.status}`);
    }

    const includedText = await includeResponse.text();
    const expandedIncludedText = await expandMjcfIncludes({
      xmlText: includedText,
      xmlPath: includePath,
      folder,
      assetId,
      resolveCatalogUrl,
      signal,
      visited,
    });
    const includedDoc = parseXmlText(expandedIncludedText);
    const includedRoot =
      includedDoc.documentElement?.tagName?.toLowerCase() === "mujoco"
        ? includedDoc.documentElement
        : includedDoc;
    const replacementNodes = Array.from(includedRoot.childNodes).filter(
      (node) => node.nodeType === globalThis.Node.ELEMENT_NODE
    );
    const parent = includeElement.parentNode;
    if (!parent) continue;
    replacementNodes.forEach((node) => {
      parent.insertBefore(xmlDoc.importNode(node, true), includeElement);
    });
    parent.removeChild(includeElement);
  }

  return new XMLSerializer().serializeToString(xmlDoc);
};

const fetchLibraryAssetFiles = async (asset, onProgress, signal) => {
  const report = (t) => {
    if (typeof onProgress === "function") onProgress(Math.min(1, Math.max(0, t)));
  };
  report(LIBRARY_ASSET_LOAD_PROGRESS.XML_FETCH_STARTED);
  libraryLoadThrowIfAborted(signal);
  const folder = asset.folder || "objects";
  const libraryPathPrefix = `/${folder}/${asset.id}/`;
  const xmlFilename = asset.xmlFilename ?? `${asset.id}.xml`;
  const resolveCatalogUrl = (path) =>
    asset.source === "critter-assets" ? resolveCritterAssetsUrl(path) : path;
  const xmlPath = resolveCatalogUrl(`/${folder}/${asset.id}/${xmlFilename}`);
  const xmlResponse = await fetch(xmlPath, { cache: "no-store", signal });
  if (!xmlResponse.ok) {
    throw new Error(`Failed to load XML for ${asset.id}`);
  }

  const xmlBlob = await xmlResponse.blob();
  const rawXmlText = await xmlBlob.text();
  const expandedXmlText = await expandMjcfIncludes({
    xmlText: rawXmlText,
    xmlPath: xmlFilename,
    folder,
    assetId: asset.id,
    resolveCatalogUrl,
    signal,
  });
  const xmlText = applyMjcfCatalogTransforms(expandedXmlText, asset);
  const xmlFile = new File([xmlText], xmlFilename, { type: "application/xml" });
  report(LIBRARY_ASSET_LOAD_PROGRESS.XML_READY);
  libraryLoadThrowIfAborted(signal);
  const xmlDoc = parseXmlText(xmlText);
  const compilerElement = xmlDoc.querySelector("compiler");
  const assetdir = normalizeAssetPath(compilerElement?.getAttribute("assetdir"), {
    fallback: "assets",
    stripTrailingSlash: true,
  });
  const meshdir = normalizeAssetPath(compilerElement?.getAttribute("meshdir"), {
    fallback: assetdir,
    stripTrailingSlash: true,
  });
  const texturedir = normalizeAssetPath(compilerElement?.getAttribute("texturedir"), {
    fallback: assetdir,
    stripTrailingSlash: true,
  });

  const meshElements = xmlDoc.querySelectorAll("mesh");
  const meshFetchTargets = [];
  for (const meshElement of meshElements) {
    const fileAttr = meshElement.getAttribute("file");
    const meshNameAttr = meshElement.getAttribute("name");
    if (!fileAttr) continue;
    const actualFilePath = joinAssetPath(meshdir, fileAttr);
    if (actualFilePath) {
      meshFetchTargets.push({ fileAttr, meshNameAttr, actualFilePath });
    }
  }

  libraryLoadThrowIfAborted(signal);

  const meshFetchResults = await fetchAssetTargets({
    targets: meshFetchTargets,
    folder,
    assetId: asset.id,
    resolveCatalogUrl,
    signal,
    report,
    progressStart: LIBRARY_ASSET_LOAD_PROGRESS.MESH_FETCH_START,
    progressSpan: LIBRARY_ASSET_LOAD_PROGRESS.MESH_FETCH_SPAN,
    buildResult: ({ fileAttr, meshNameAttr, actualFilePath }, meshBlob) => {
      const meshFile = new File([meshBlob], actualFilePath, { type: meshBlob.type });
      assertMeshFileWithinBrowserImportLimit(meshFile, actualFilePath);
      const meshUuid = generateUuid();
      const meshName =
        meshNameAttr || fileAttr.replace(/^.*[/\\]/, "").replace(/\.(obj|stl)$/i, "");
      return { meshFile, actualFilePath, meshUuid, meshName, fileAttr };
    },
  });

  libraryLoadThrowIfAborted(signal);

  const rejectedMeshLimitErrors = meshFetchResults
    .filter((result) => result.status === "rejected" && isBrowserImportLimitError(result.reason))
    .map((result) => result.reason);
  if (rejectedMeshLimitErrors.length > 0) {
    const shouldProceed = await requestBrowserImportLimitProceedForIssue(
      browserImportLimitIssueFromErrors(rejectedMeshLimitErrors)
    );
    if (!shouldProceed) {
      throw rejectedMeshLimitErrors[0];
    }
  }

  const meshFiles = [];
  const meshFilesBySourcePath = {};
  const meshAliases = [];
  const failedMeshNames = [];
  for (const result of meshFetchResults) {
    if (result.status === "rejected") {
      if (isBrowserImportLimitError(result.reason)) {
        const skippedMeshName = result.reason.fileName || "oversized mesh file";
        failedMeshNames.push(skippedMeshName);
        logger.warn(`[libraryAssetLoader] Proceeding without rejected mesh: ${skippedMeshName}`);
        continue;
      }
      failedMeshNames.push(result.reason?.fileName || result.reason?.message || "mesh file");
      logger.warn("[libraryAssetLoader] Failed to load mesh file:", result.reason);
      continue;
    }
    const { meshFile, actualFilePath, meshUuid, meshName, fileAttr } = result.value;
    meshFiles.push(meshFile);
    meshFilesBySourcePath[actualFilePath] = meshFile;
    meshAliases.push({ meshUuid, meshName, sourceName: actualFilePath, originalFile: fileAttr });
  }
  showLibraryMeshLoadWarning(failedMeshNames);

  if (meshFetchTargets.length === 0) {
    report(LIBRARY_ASSET_LOAD_PROGRESS.MESH_FETCH_COMPLETE);
  }

  const textureElements = xmlDoc.querySelectorAll("texture");
  const textureFetchTargets = [];
  for (const textureElement of textureElements) {
    const fileAttr = textureElement.getAttribute("file");
    if (!fileAttr) continue;
    const actualFilePath = joinAssetPath(texturedir, fileAttr);
    textureFetchTargets.push({ actualFilePath });
  }

  const textureFetchResults = await fetchAssetTargets({
    targets: textureFetchTargets,
    folder,
    assetId: asset.id,
    resolveCatalogUrl,
    signal,
    report,
    progressStart: LIBRARY_ASSET_LOAD_PROGRESS.TEXTURE_FETCH_START,
    progressSpan: LIBRARY_ASSET_LOAD_PROGRESS.TEXTURE_FETCH_SPAN,
    buildResult: ({ actualFilePath }, textureBlob) => {
      const textureFile = new File([textureBlob], actualFilePath, {
        type: textureBlob.type || "image/png",
      });
      return { textureFile, actualFilePath };
    },
  });

  const textureFiles = [];
  const textureFilesBySourcePath = {};
  for (const result of textureFetchResults) {
    if (result.status === "rejected") {
      logger.warn("[libraryAssetLoader] Failed to load texture file:", result.reason);
      continue;
    }
    const { textureFile, actualFilePath } = result.value;
    textureFiles.push(textureFile);
    textureFilesBySourcePath[actualFilePath] = textureFile;
  }

  if (textureFetchTargets.length === 0) {
    report(LIBRARY_ASSET_LOAD_PROGRESS.DONE);
  }

  libraryLoadThrowIfAborted(signal);

  return {
    folder,
    libraryPathPrefix,
    xmlFile,
    meshFiles,
    meshFilesBySourcePath,
    meshAliases,
    textureFiles,
    textureFilesBySourcePath,
  };
};

const mergeIntoFileMap = ({
  libraryPathPrefix,
  xmlFile,
  meshFiles,
  meshFilesBySourcePath,
  meshAliases,
  textureFiles,
  textureFilesBySourcePath,
}) => {
  const currentFileMap = useFileStore.getState().fileMap || {};
  const newFileMap = { ...currentFileMap };
  const allFiles = [xmlFile, ...meshFiles, ...textureFiles];
  for (const file of allFiles) {
    newFileMap[file.name] = file;
  }
  Object.entries(meshFilesBySourcePath).forEach(([sourcePath, meshFile]) => {
    const scopedPath = libraryPathPrefix ? `${libraryPathPrefix}${sourcePath}` : sourcePath;
    const scopedPathNoLead = scopedPath.replace(/^\/+/, "");
    newFileMap[scopedPath] = meshFile;
    newFileMap[scopedPathNoLead] = meshFile;
    newFileMap[sourcePath] = meshFile;
    const baseName = sourcePath.replace(/^.*\//, "");
    if (baseName && !newFileMap[baseName]) newFileMap[baseName] = meshFile;
  });
  Object.entries(textureFilesBySourcePath).forEach(([sourcePath, textureFile]) => {
    const scopedPath = libraryPathPrefix ? `${libraryPathPrefix}${sourcePath}` : sourcePath;
    const scopedPathNoLead = scopedPath.replace(/^\/+/, "");
    newFileMap[scopedPath] = textureFile;
    newFileMap[scopedPathNoLead] = textureFile;
    newFileMap[sourcePath] = textureFile;
    const baseName = sourcePath.replace(/^.*\//, "");
    if (baseName && !newFileMap[baseName]) newFileMap[baseName] = textureFile;
  });
  for (const alias of meshAliases) {
    const srcName = alias.sourceName;
    const scopedSrcName = libraryPathPrefix ? `${libraryPathPrefix}${srcName}` : srcName;
    const aliasObj = newFileMap[scopedSrcName] || newFileMap[srcName];
    if (!aliasObj) continue;
    const ext =
      alias.originalFile?.match(/\.(obj|stl)$/i)?.[1]?.toLowerCase() ||
      srcName.match(/\.(obj|stl)$/i)?.[1]?.toLowerCase() ||
      "obj";
    const uuidWithExt = `${alias.meshUuid}.${ext}`;
    const nameWithExt = `${alias.meshName}.${ext}`;
    const assetsNameWithExt = `assets/${alias.meshName}.${ext}`;
    const originalFileAlias = alias.originalFile;
    const scopedUuidWithExt = libraryPathPrefix
      ? `${libraryPathPrefix}${uuidWithExt}`
      : uuidWithExt;
    const scopedNameWithExt = libraryPathPrefix
      ? `${libraryPathPrefix}${nameWithExt}`
      : nameWithExt;
    const scopedAssetsNameWithExt = libraryPathPrefix
      ? `${libraryPathPrefix}${assetsNameWithExt}`
      : assetsNameWithExt;
    newFileMap[scopedUuidWithExt] = aliasObj;
    newFileMap[scopedUuidWithExt.replace(/^\/+/, "")] = aliasObj;
    newFileMap[scopedNameWithExt] = aliasObj;
    newFileMap[scopedNameWithExt.replace(/^\/+/, "")] = aliasObj;
    newFileMap[scopedAssetsNameWithExt] = aliasObj;
    newFileMap[scopedAssetsNameWithExt.replace(/^\/+/, "")] = aliasObj;
    if (!newFileMap[uuidWithExt]) newFileMap[uuidWithExt] = aliasObj;
    if (!newFileMap[nameWithExt]) newFileMap[nameWithExt] = aliasObj;
    if (!newFileMap[assetsNameWithExt]) newFileMap[assetsNameWithExt] = aliasObj;
    if (originalFileAlias && originalFileAlias !== srcName && !newFileMap[originalFileAlias]) {
      newFileMap[originalFileAlias] = aliasObj;
    }
  }
  getFileActions().setFileMap(newFileMap);
  return newFileMap;
};

const remapParsedMeshes = ({
  parsedResult,
  parsedBodiesMap,
  meshAliases,
  libraryPathPrefix = "",
}) => {
  const meshNameToUuid = new Map();
  meshAliases.forEach((alias) => {
    meshNameToUuid.set(alias.meshName, alias.meshUuid);
  });

  if (!parsedResult.meshes) return;

  const originalMeshes = { ...parsedResult.meshes };
  const meshNameToPath = new Map();
  meshAliases.forEach((alias) => {
    const sourcePath = libraryPathPrefix
      ? `${libraryPathPrefix}${alias.sourceName}`
      : alias.sourceName;
    meshNameToPath.set(alias.meshName, sourcePath);
  });

  const meshesAlreadyUuid = Object.values(originalMeshes).every(
    (meshDef) => meshDef && meshDef.uuid
  );
  if (meshesAlreadyUuid) {
    const nameToUuidMap = new Map();
    const retainedMeshUuids = new Set();
    const meshesByUuid = {};
    Object.values(originalMeshes).forEach((meshDef) => {
      if (!meshDef?.name || !meshDef.uuid) return;
      const url = meshNameToPath.get(meshDef.name);
      if (!url) return;
      nameToUuidMap.set(meshDef.name, meshDef.uuid);
      retainedMeshUuids.add(meshDef.uuid);
      meshesByUuid[meshDef.uuid] = {
        ...meshDef,
        url,
      };
    });
    for (const body of Object.values(parsedBodiesMap)) {
      if (!body || !body.geoms) continue;
      body.geoms = body.geoms.filter((geom) => {
        if (geom && typeof geom.mesh === "string") {
          const remappedUuid = nameToUuidMap.get(geom.mesh);
          if (remappedUuid) {
            geom.mesh = remappedUuid;
            return true;
          }
          return retainedMeshUuids.has(geom.mesh);
        }
        return true;
      });
    }
    const omittedMeshCount = Object.keys(originalMeshes).length - retainedMeshUuids.size;
    if (omittedMeshCount > 0) {
      logger.warn(
        `[libraryAssetLoader] Omitted ${omittedMeshCount} mesh references that were not loaded.`
      );
    }
    parsedResult.meshes = meshesByUuid;
    return;
  }

  const nameToUuidMap = new Map();
  const skippedMeshNames = [];
  Object.keys(originalMeshes).forEach((name) => {
    if (meshNameToUuid.has(name)) {
      nameToUuidMap.set(name, meshNameToUuid.get(name));
      return;
    }
    skippedMeshNames.push(name);
  });

  for (const body of Object.values(parsedBodiesMap)) {
    if (!body || !body.geoms) continue;
    body.geoms = body.geoms.filter((geom) => {
      if (geom && typeof geom.mesh === "string") {
        if (nameToUuidMap.has(geom.mesh)) {
          const remappedUuid = nameToUuidMap.get(geom.mesh);
          if (remappedUuid) geom.mesh = remappedUuid;
          return true;
        } else {
          return false;
        }
      }
      return true;
    });
  }

  if (skippedMeshNames.length > 0) {
    logger.warn(
      `[libraryAssetLoader] Omitted ${skippedMeshNames.length} mesh references that were not loaded.`
    );
  }

  const meshesByUuid = {};
  Object.entries(originalMeshes).forEach(([name, meshDef]) => {
    if (!meshDef) return;
    const uuid = nameToUuidMap.get(name);
    if (!uuid) return;
    const url = meshNameToPath.get(name) || meshDef.url;
    meshesByUuid[uuid] = {
      ...meshDef,
      uuid,
      name: meshDef.name || name,
      url: url || meshDef.url,
    };
  });
  parsedResult.meshes = meshesByUuid;
};

export const loadLibraryAsset = async (asset, options = {}) => {
  if (!asset?.id) {
    throw new Error("loadLibraryAsset requires an asset with an id.");
  }
  if (shouldBlockSceneAuthoringMutation("loadLibraryAsset")) return null;

  const {
    restartPhysics = true,
    selectLoadedBody = true,
    transformOverride = null,
    preserveSpawnKeyframeTransform = true,
    saveToHistory = true,
    onProgress,
    signal = null,
    /** Callback when the loaded asset has been merged into the scene (for cancel/rollback bookkeeping). */
    onInsertedLibraryRoot = null,
  } = options;

  const prog = (u) => {
    if (typeof onProgress === "function") onProgress(Math.min(1, Math.max(0, u)));
  };

  const wasPhysicsRunning = restartPhysics ? usePhysicsStore.getState().isRunning : false;
  let fetched;
  try {
    fetched = await fetchLibraryAssetFiles(
      asset,
      (t) => prog(t * LIBRARY_ASSET_LOAD_PROGRESS.FILE_FETCH_PHASE_END),
      signal
    );
  } catch (error) {
    if (!isBrowserImportLimitError(error)) {
      showBrowserImportLimitIssue(error);
    }
    throw error;
  }
  prog(LIBRARY_ASSET_LOAD_PROGRESS.FILES_FETCHED);
  libraryLoadThrowIfAborted(signal);
  const newFileMap = mergeIntoFileMap(fetched);
  prog(LIBRARY_ASSET_LOAD_PROGRESS.FILES_MERGED);
  libraryLoadThrowIfAborted(signal);
  const mjcfParser = new MJCFParser();
  prog(LIBRARY_ASSET_LOAD_PROGRESS.PARSER_READY);
  const parsedResult = await mjcfParser.parseFile(fetched.xmlFile);
  await applyPhysicsTimestepFromXmlFile(fetched.xmlFile, asset.name || fetched.xmlFile.name);
  prog(LIBRARY_ASSET_LOAD_PROGRESS.PARSED);
  libraryLoadThrowIfAborted(signal);
  const shouldContinueAfterWarningPrompt = await promptForLibraryAssetImportWarnings({
    asset,
    fetched,
    parsedResult,
  });
  if (!shouldContinueAfterWarningPrompt) {
    await restartPhysicsIfNeeded(wasPhysicsRunning);
    prog(LIBRARY_ASSET_LOAD_PROGRESS.DONE);
    return { rootBodyUuid: null, ownerLabel: asset.name };
  }
  libraryLoadThrowIfAborted(signal);
  const parsedBodiesMap = parsedResult.bodies || {};
  const parsedWorldBodies = parsedResult.worldBodies || [];

  if (Object.keys(parsedBodiesMap).length === 0) {
    await restartPhysicsIfNeeded(wasPhysicsRunning);
    prog(LIBRARY_ASSET_LOAD_PROGRESS.DONE);
    return { rootBodyUuid: null, ownerLabel: asset.name };
  }

  const collisionBit = nextCollisionTypeBit++;
  remapParsedMeshes({
    parsedResult,
    parsedBodiesMap,
    meshAliases: fetched.meshAliases,
    libraryPathPrefix: fetched.libraryPathPrefix,
  });

  const rootBodyUuid = getRootBodyUuid(parsedWorldBodies, parsedBodiesMap);
  const sceneActions = getSceneActions();

  await loadSceneFromParsedResult(parsedResult, sceneActions, {
    resetScene: false,
    mergeWithExisting: true,
    assignCollisionTypes: true,
    collisionBitStart: collisionBit,
    deferCollisionMeshProcessing: false,
    adjustPositions: false,
    folderType: fetched.folder,
    fileMap: newFileMap,
    saveToHistory,
    worldBodyNameBase: asset.name,
    meshLibraryPathPrefix: fetched.libraryPathPrefix,
  });
  prog(LIBRARY_ASSET_LOAD_PROGRESS.SCENE_MERGED);
  if (rootBodyUuid && typeof onInsertedLibraryRoot === "function") {
    onInsertedLibraryRoot(rootBodyUuid);
  }
  libraryLoadThrowIfAborted(signal);

  if (fetched.folder === "objects" && rootBodyUuid) {
    const body = getSceneState().bodies?.[rootBodyUuid];
    if (
      body &&
      Array.isArray(body.position) &&
      body.position.length === 3 &&
      Array.isArray(body.orientation) &&
      body.orientation.length === 4
    ) {
      recordMjcfRootDefaultPose(rootBodyUuid, [...body.position], [...body.orientation]);
    }
  }

  const parsedKeyframes = parsedResult.keyframes || {};
  let ownerLabel = asset.name;
  let appliedSpawnKeyframe = false;
  if (rootBodyUuid) {
    const worldBodyEntry = (getSceneState().worldBodies || []).find((wb) =>
      typeof wb === "string" ? wb === rootBodyUuid : wb?.uuid === rootBodyUuid
    );
    ownerLabel =
      (typeof worldBodyEntry === "string" ? worldBodyEntry : worldBodyEntry?.name) || asset.name;
  }

  if (Object.keys(parsedKeyframes).length > 0) {
    const scopedKeyframes = {};
    Object.entries(parsedKeyframes).forEach(([keyframeName, qpos]) => {
      scopedKeyframes[`${ownerLabel} :: ${keyframeName}`] = qpos;
    });
    getFileActions().mergeKeyframes(scopedKeyframes);

    const explicitSpawn =
      asset.spawnKeyframe != null && String(asset.spawnKeyframe).trim() !== ""
        ? String(asset.spawnKeyframe).trim()
        : null;
    const spawnKeyframeName =
      explicitSpawn ??
      (fetched.folder === "robots" && Object.prototype.hasOwnProperty.call(parsedKeyframes, "home")
        ? "home"
        : DEFAULT_SPAWN_KEYFRAME_NAME);
    if (
      (fetched.folder === "objects" || fetched.folder === "robots") &&
      rootBodyUuid &&
      Object.prototype.hasOwnProperty.call(parsedKeyframes, spawnKeyframeName)
    ) {
      await getFileActions().handleKeyframeSelect(
        `${ownerLabel} :: ${spawnKeyframeName}`,
        rootBodyUuid,
        {
          preserveWorldRootPose: false,
        }
      );
      appliedSpawnKeyframe = true;
    }
  }
  prog(LIBRARY_ASSET_LOAD_PROGRESS.KEYFRAMES_APPLIED);
  libraryLoadThrowIfAborted(signal);

  if (rootBodyUuid && transformOverride) {
    const currentBody = getSceneState().bodies?.[rootBodyUuid];
    const normalizedTransform = normalizeTransformOverride(transformOverride, currentBody);
    if (normalizedTransform) {
      const nextPosition = [...normalizedTransform.position];
      const nextOrientation = [...normalizedTransform.orientation];
      // If a spawn/default keyframe was applied, preserve the keyframed vertical
      // placement (and orientation) and only place by dragged x/y. This keeps
      // drag-load behavior consistent with click-load defaults.
      if (
        preserveSpawnKeyframeTransform &&
        appliedSpawnKeyframe &&
        currentBody &&
        Array.isArray(currentBody.position) &&
        currentBody.position.length === 3 &&
        Number.isFinite(Number(currentBody.position[2]))
      ) {
        nextPosition[2] = Number(currentBody.position[2]);
      }
      if (
        preserveSpawnKeyframeTransform &&
        !appliedSpawnKeyframe &&
        fetched.folder === "robots" &&
        currentBody &&
        Array.isArray(currentBody.position) &&
        currentBody.position.length === 3 &&
        Number.isFinite(Number(currentBody.position[2])) &&
        Number.isFinite(Number(normalizedTransform.position[2]))
      ) {
        nextPosition[2] = Number(normalizedTransform.position[2]) + Number(currentBody.position[2]);
      }
      if (
        preserveSpawnKeyframeTransform &&
        appliedSpawnKeyframe &&
        currentBody &&
        Array.isArray(currentBody.orientation) &&
        currentBody.orientation.length === 4 &&
        currentBody.orientation.every((value) => Number.isFinite(Number(value)))
      ) {
        nextOrientation[0] = Number(currentBody.orientation[0]);
        nextOrientation[1] = Number(currentBody.orientation[1]);
        nextOrientation[2] = Number(currentBody.orientation[2]);
        nextOrientation[3] = Number(currentBody.orientation[3]);
      }
      sceneActions.setBodyTransform(rootBodyUuid, nextPosition, nextOrientation);
    }
  }
  prog(LIBRARY_ASSET_LOAD_PROGRESS.TRANSFORM_APPLIED);

  if (rootBodyUuid && selectLoadedBody) {
    const selectionActions = useSelectionStore.getState();
    selectionActions.setLastSelectionSource("objectLibrary");
    selectionActions.selectBody(null);
    selectionActions.selectBody(rootBodyUuid);
  }
  prog(LIBRARY_ASSET_LOAD_PROGRESS.SELECTION_APPLIED);

  if (rootBodyUuid && libraryAssetHasGrasps(asset)) {
    getGraspActions().loadGraspsForBody(rootBodyUuid, asset.id, { source: asset.source });
  }
  prog(LIBRARY_ASSET_LOAD_PROGRESS.GRASPS_LOADED);
  libraryLoadThrowIfAborted(signal);

  await restartPhysicsIfNeeded(wasPhysicsRunning);
  showNoRobotVisualsNoticeIfNeeded(parsedResult, {
    requireRobotFeatures: asset.category !== "robot",
  });
  prog(LIBRARY_ASSET_LOAD_PROGRESS.DONE);
  return {
    rootBodyUuid,
    ownerLabel,
    keyframeNames: Object.keys(parsedKeyframes),
  };
};
